import {MinimalCutSet} from './minimal-cut-sets';

export type RiskZone = 'Low' | 'Moderate' | 'High' | 'Critical';

/** Adjusted probability breakdown passed from the sensitivity explorer to the risk gauge. */
export interface AdjustedRiskProbabilities {
    totalProbability: number;
    order1Probability: number;
    hubProbability: number;
    order1to3Probability: number;
}

export interface RiskWeights {
    logScore: number;
    spfPenalty: number;
    hubPenalty: number;
    orderConcentration: number;
}

export const DEFAULT_RISK_WEIGHTS: RiskWeights = {
    logScore: 0.50,
    spfPenalty: 0.25,
    hubPenalty: 0.15,
    orderConcentration: 0.10,
};

export interface InherentRiskScore {
    composite: number;
    logScore: number;
    spfPenalty: number;
    hubPenalty: number;
    orderConcentration: number;
    riskZone: RiskZone;
    riskColor: string;
}

const ZONE_THRESHOLDS: Array<{ max: number; zone: RiskZone; color: string }> = [
    {max: 25, zone: 'Low', color: '#4caf50'},
    {max: 50, zone: 'Moderate', color: '#f9a825'},
    {max: 75, zone: 'High', color: '#e65100'},
    {max: 100, zone: 'Critical', color: '#d32f2f'},
];

function clamp(value: number, min: number, max: number): number {
    return Math.min(max, Math.max(min, value));
}

function getZone(score: number): { zone: RiskZone; color: string } {
    for (const t of ZONE_THRESHOLDS) {
        if (score <= t.max) return {zone: t.zone, color: t.color};
    }
    return {zone: 'Critical', color: '#d32f2f'};
}

/** Pre-computed probability sums for each sub-score category (used by sensitivity explorer). */
interface PrecomputedProbabilities {
    order1Probability: number;
    hubProbability: number;
    order1to3Probability: number;
}

export function computeInherentRiskScore(
    cutSets: MinimalCutSet[],
    totalProbability: number,
    totalBaseEvents: number,
    structuralHubIds: Set<number>,
    weights: RiskWeights = DEFAULT_RISK_WEIGHTS,
    precomputed?: PrecomputedProbabilities,
): InherentRiskScore {
    // logScore: maps P(top) on a log scale — 1e-11→0 … 0.1→100
    // Aviation probabilities commonly range from 1e-11 to 1e-1
    const logScore = totalProbability > 0
        ? clamp((Math.log10(totalProbability) + 11) / 10 * 100, 0, 100)
        : 0;

    // spfPenalty: fraction of risk from order-1 cut sets
    const order1Prob = precomputed
        ? precomputed.order1Probability
        : cutSets
            .filter((cs) => cs.order === 1)
            .reduce((sum, cs) => sum + cs.probability, 0);
    const spfPenalty = totalProbability > 0
        ? clamp((order1Prob / totalProbability) * 100, 0, 100)
        : 0;

    // hubPenalty: fraction of risk from cut sets containing structural hubs
    const hubProb = precomputed
        ? precomputed.hubProbability
        : structuralHubIds.size > 0
            ? cutSets
                .filter((cs) => {
                    for (const id of cs.baseEventIds) {
                        if (structuralHubIds.has(id)) return true;
                    }
                    return false;
                })
                .reduce((sum, cs) => sum + cs.probability, 0)
            : 0;
    const hubPenalty = totalProbability > 0
        ? clamp((hubProb / totalProbability) * 100, 0, 100)
        : 0;

    // orderConcentration: risk from order-1, order-2, and order-3 cut sets
    const order1to3Prob = precomputed
        ? precomputed.order1to3Probability
        : cutSets
            .filter((cs) => cs.order <= 3)
            .reduce((sum, cs) => sum + cs.probability, 0);
    const orderConcentration = totalProbability > 0
        ? clamp((order1to3Prob / totalProbability) * 100, 0, 100)
        : 0;

    const composite = clamp(
        weights.logScore * logScore + weights.spfPenalty * spfPenalty + weights.hubPenalty * hubPenalty + weights.orderConcentration * orderConcentration,
        0,
        100,
    );

    const {zone, color} = getZone(composite);

    return {
        composite,
        logScore,
        spfPenalty,
        hubPenalty,
        orderConcentration,
        riskZone: zone,
        riskColor: color,
    };
}
